import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Code2, ArrowRight } from 'lucide-react';
import { integrationCategories } from '../data/mockData';
import IntegrationLogo from '../components/IntegrationLogo';
import SEO from '../components/SEO';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import { useScrollReveal } from '../hooks/useScrollReveal';

const CategoryBlock = ({ category, index }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section
      ref={ref}
      id={category.id}
      data-testid={`integration-category-${category.id}`}
      className={`transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="grid md:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Sidebar */}
        <div className="md:col-span-4 md:sticky md:top-28">
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {String(index + 1).padStart(2, '0')} / Integration
          </p>
          <h2
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {category.title}
          </h2>
          <p
            className="text-base text-gray-600 leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {category.description}
          </p>
        </div>

        {/* Logo grid or API card */}
        <div className="md:col-span-8">
          {category.isApi ? (
            <div className="rounded-3xl bg-gradient-to-br from-[#00BCE4] to-[#0a0b10] text-white p-10 lg:p-12 shadow-xl">
              <Code2 size={40} className="mb-6 opacity-80" />
              <h3
                className="text-2xl lg:text-3xl font-bold mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Build your own with the Imprint API
              </h3>
              <p
                className="text-base leading-relaxed mb-8 text-white/85"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                RESTful, JSON, well-documented. Connect Imprint MIS to your ERP,
                CRM, in-house dashboards or any third-party service. Our team will
                work alongside yours to scope, spec and support your integration.
              </p>
              <Link
                to="/#contact"
                data-testid="integrations-api-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0a0b10] text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Talk to our team
                <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {category.partners.map((partner) => (
                <IntegrationLogo key={partner.name} partner={partner} size="lg" />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const IntegrationsPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="pt-0 lg:pt-28 pb-20 bg-white min-h-screen">
      <SEO
        title="Integration Partners"
        description="Imprint MIS integrates with the tools you already run — accounting, pre-press, web-to-print, machine data, shipping and more. Plus a full RESTful API."
        url="https://imprint-mis.co.uk/integrations"
      />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Integrations', path: '/integrations' },
      ])} />

      {/* Hero */}
      <section className="bg-gray-50 border-b border-gray-100 py-16 lg:py-24">
        <div className="section-container">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#00BCE4] transition-colors mb-6"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Works With Your Stack
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Integration <span className="text-[#00BCE4]">Partners</span>
          </h1>
          <p
            className="text-lg text-gray-600 leading-relaxed max-w-3xl"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Imprint MIS is built to slot into your existing stack. From CIP4/JDF
            production workflows to accounting, web-to-print, machine monitoring
            and carrier integrations — we&apos;ve done the plumbing so you don&apos;t
            have to.
          </p>

          {/* Category anchor pills */}
          <div className="mt-10 flex flex-wrap gap-2">
            {integrationCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="px-4 py-2 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:border-[#00BCE4] hover:text-[#00BCE4] transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {cat.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 lg:py-28">
        <div className="section-container space-y-20 lg:space-y-28">
          {integrationCategories.map((cat, index) => (
            <CategoryBlock key={cat.id} category={cat} index={index} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default IntegrationsPage;
